import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { IBSAdonisService } from "../Services/IBSAdonisService";
import { IBodyshopService } from "../UserService";
import Modal, { Styles } from "react-modal";
import ImageListItem from "./ImageListItem";
import { JobEstimationDetail } from "../modals/JobEstimationDetail";
import { Mixpanel } from "../Services/MixPanel";
const cameraSvg = require("../assets/images/svg/camera.svg").default;
const gallerySvg = require("../assets/images/svg/gallery.svg").default;
const qualitySvg = require("../assets/images/svg/quality-video.svg").default;
const dustbinSvg = require("../assets/images/svg/dustbin.svg").default;

export interface IUploadObject {
  Id: string;
  files: Object;
  updateTrackableRequest: Object;
}

export interface IFile {
  FileName: string;
  Base64Content: string;
  imageSize?: string | null;
  id: string;
  listId: string;
  deleting?: boolean;
}

const modalStyles = {
  content: {
    height: "167px",
    width: "346px",
    margin: "auto",
    position: "absolute",
    top: "0%",
    left: "0%",
    right: "0%",
    bottom: "0%",
    background: "#fff",
    borderRadius: "10px",
    outline: "none",
  },
  overlay: {
    background: "rgba(0, 0, 0, 0.5)",
  },
};

interface UploadPhotosProps {
  filesArray: IFile[];
  setFilesArray: React.Dispatch<React.SetStateAction<IFile[]>>;
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>;
  isOptional?: boolean;
}

const UploadPhotos: React.FC<UploadPhotosProps> = ({
  filesArray,
  setFilesArray,
  setIsDeleting,
  isOptional = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const webcamRef = useRef<Webcam | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localIsDeleting, setLocalIsDeleting] = useState(false);
  const [estimateId, setEstimateId] = useState("");
  const [estimateDetails, setEstimateDetails] = useState<JobEstimationDetail>();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const params = useParams();
  const ibsAdonisService = new IBSAdonisService();
  const iBodyShopService = new IBodyshopService();

  const location = useLocation();

  // Get query parameters from the URL
  const searchParams = new URLSearchParams(location.search);
  const estimateIdParam = searchParams.get("estimateid");
  const stepParam = searchParams.get("step");

  const getImages = async (showLoader: boolean = false) => {
    const id = params.id || estimateIdParam;
    if (showLoader) {
      setIsFetchingImages(true);
    }
    if (id) {
      try {
        const estimateResponse: any =
          await ibsAdonisService.getJobEstimateDetailsByIds([id]);
        const estimate = estimateResponse[0];
        setEstimateDetails(estimate);
        setEstimateId(estimate.estimateId);

        const imagesResponse = await ibsAdonisService.getImagesByEstimationId(
          estimate.estimateId
        );
        const images = await Promise.all(
          imagesResponse[0].images.map(async (img, i) => {
            const highResImg = ibsAdonisService.getHighResolutionImageUrl(
              img.full.id
            );
            const imageSize = await checkImageSize(highResImg);
            return {
              FileName: `Image-${i + 1}`,
              Base64Content: highResImg,
              imageSize,
              id: img.id,
              listId: imagesResponse[0].id,
              deleting: false,
            } as IFile;
          })
        );
        if (showLoader) {
          setIsFetchingImages(false);
        }
        return images;
      } catch (error) {
        console.error("Error fetching images or estimate details:", error);
        if (showLoader) {
          setIsFetchingImages(false);
        }
        return [];
      }
    } else {
      if (showLoader) {
        setIsFetchingImages(false);
      }
      return [];
    }
  };

  const captureImage = useCallback(async () => {
    setLoading(true);
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setIsCameraOpen(false);
      const imageSize = await checkImageSize(imageSrc);
      const base64String = imageSrc;
      const base64Content = base64String.split(",")[1];

      const fileObj: IFile = {
        FileName: `Image-${filesArray.length + 1}`,
        Base64Content: imageSrc,
        id: "",
        listId: params.id || "",
        imageSize,
      };

      const uploadPayload = {
        Id: estimateId,
        files: [
          {
            FileName: `Image-${filesArray.length + 1}`,
            Base64Content: base64Content,
          },
        ],
        updateTrackableRequest: {},
      };

      const iBodyshopService = new IBodyshopService();
      try {
        const res = await ibsAdonisService.addImagesBase64(
          uploadPayload,
          "CustomerUpload"
        );
        if (res.status === 200) {
          Mixpanel.track("Image Uploaded", {
            fileName: fileObj.FileName,
            fileSize: imageSize,
            listId: params.id,
            uploadTime: new Date().toISOString(),
          });
          setFilesArray((prevFiles) => [...prevFiles, fileObj]);
        } else {
          console.error("Upload failed:", res);
        }
      } catch (err) {
        console.error("Error uploading captured image:", err);
      }
    }
    setLoading(false);
  }, [filesArray.length, params.id, setFilesArray, estimateId]);

  const handleArchiveImages = async (
    ids: string[],
    listId: string,
    index: number
  ) => {
    setIsDeleting(true);
    setFilesArray((prevFilesArray) =>
      prevFilesArray.map((file, i) =>
        i === index ? { ...file, deleting: true } : file
      )
    );
    try {
      if (ids.length) {
        const archiveResponse: any =
          await ibsAdonisService.archiveImagesByEstimationIds(listId, ids);
        if (archiveResponse?.data?.success) {
          Mixpanel.track("Image Deleted", {
            imageId: ids[0],
            listId: listId,
            deletionTime: new Date().toISOString(),
          });
          setFilesArray((prevFiles) =>
            prevFiles.filter((file) => file.id !== ids[0])
          );
        }
      } else {
        const res = await getImages();
        setFilesArray(res as IFile[]);
        const imageToDelete = (res as IFile[])[index];
        await ibsAdonisService.archiveImagesByEstimationIds(
          imageToDelete.listId,
          [imageToDelete.id]
        );
        Mixpanel.track("Image Deleted", {
          imageId: imageToDelete.id,
          listId: imageToDelete.listId,
          deletionTime: new Date().toISOString(),
        });
        const newArray = [...filesArray];
        newArray.splice(index, 1);
        setFilesArray(newArray);
      }
    } catch (err) {
      console.error("Error archiving images:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteAllImages = () => {
    setDeleteModal(false);
    if (filesArray.length) {
      setIsDeleting(true);
      setLocalIsDeleting(true);
      getImages()
        .then(async (res) => {
          const listId = (res as IFile[])[0]?.listId;
          const ids = (res as IFile[]).map((file) => file.id);
          const archiveResponse: any =
            await ibsAdonisService.archiveImagesByEstimationIds(listId, ids);
          if (archiveResponse["data"]["success"]) {
            Mixpanel.track("All Images Deleted", {
              listId: listId,
              totalImagesDeleted: ids.length,
              deletionTime: new Date().toISOString(),
            });
            setFilesArray([]);
            setIsDeleting(false);
            setLocalIsDeleting(false);
          }
        })
        .catch(() => {
          setIsDeleting(false);
          setLocalIsDeleting(false);
        });
    }
  };

  const checkImageSize = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error("Image not found");
      }
      const blob = await response.blob();
      const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
      return `${sizeMB}MB`;
    } catch (error) {
      return null;
    }
  };

  const sendTeamsNotification = async () => {
    if (estimateDetails) {
      const htmlContent = `<p> <strong>Images for digital assessment against estimate </strong> ${estimateDetails?.estimateNumber} <br>
      <strong>Estimator: </strong> @${estimateDetails.estimatorName}<br>
      <strong>IBS:</strong> <a href="https://app.ibodyshop.com/#/Estimate/${estimateDetails.estimateId}/header">link</a> </p>`;
      await ibsAdonisService.sendTeamsNotification({
        message: htmlContent,
        email: estimateDetails.email,
        note: {
          message: `Images for Digital Assessment`,
          estimateId: estimateDetails.estimateId,
          title: `Images For Digital Assessment`,
          estimateNumber: estimateDetails.estimateNumber,
        },
      });
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);
    const iBodyshopService = new IBodyshopService();
    const files = event.target.files;
    if (files && files.length > 0) {
      const filePromises: Promise<IFile>[] = Array.from(files).map(
        (file, index) => {
          return new Promise(async (resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = async () => {
              const base64String = reader.result as string;
              const base64Content = base64String.split(",")[1];
              const fileSizeInMB = file.size / (1024 * 1024);
              const fileObj = {
                FileName: `Image-${filesArray?.length + index + 1}`,
                Base64Content: base64String,
                id: "",
                listId: estimateId,
                imageSize: `${fileSizeInMB.toFixed(2)}MB`,
              };

              const uploadPayload = {
                Id: estimateId,
                files: [
                  {
                    FileName: file.name,
                    Base64Content: base64Content,
                  },
                ],
                updateTrackableRequest: {},
              };

              try {
                const res = await ibsAdonisService.addImagesBase64(
                  uploadPayload,
                  "CustomerUpload"
                );
                if (res.status == 200) {
                  Mixpanel.track("Image Uploaded", {
                    fileName: fileObj.FileName,
                    fileSize: fileObj.imageSize,
                    listId: params.id,
                    uploadTime: new Date().toISOString(),
                  });
                  resolve(fileObj);
                } else {
                  reject(new Error("Upload failed"));
                }
              } catch (err) {
                console.error("Error uploading image:", err);
                reject(err);
              }
            };

            reader.onerror = (error) => {
              console.error("Error reading file:", error);
              reject(error);
            };
          });
        }
      );

      try {
        const uploadedFiles: IFile[] = await Promise.all(filePromises);
        setFilesArray((prevFiles) => [...prevFiles, ...uploadedFiles]);
        if (!filesArray.length) {
          await sendTeamsNotification();
        }
      } catch (err) {
        console.error("Error uploading one or more images:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  const openCamera = () => {
    setIsCameraOpen(true);
  };

  useEffect(() => {
    getImages(true).then((images) => {
      setFilesArray((images as IFile[]) || []);
    });
  }, []);

  const videoConstraints = {
    facingMode: { exact: "environment" },
  };
  return (
    <>
      <div className="upload-photos-main">
        <p className="photos-main-heading">
          Upload Photos {isOptional && "(Optional)"}
        </p>
        <p className="description">
          Please ensure that the photos are clear, well-lit, and <br />
          capture the damage from multiple angles to provide <br />a
          comprehensive view.
        </p>
        <div className="take-photo-main">
          <div className="action-box" onClick={openCamera}>
            {!loading ? (
              <>
                <img src={cameraSvg} className="camera-img" alt="camera icon" />
                <p>Take Photo</p>
              </>
            ) : (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
          <div className="action-box" onClick={handleClick}>
            {!isLoading ? (
              <>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <img
                  src={gallerySvg}
                  className="gallery-img"
                  alt="gallery icon"
                />
                <p>Pick From Gallery</p>
              </>
            ) : (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {isCameraOpen && (
          <Modal isOpen={isCameraOpen} className="modal-camera">
            <Webcam
              ref={webcamRef}
              videoConstraints={videoConstraints}
              screenshotFormat="image/png"
              className="camera-view"
              screenshotQuality={1}
            />
            <div className="camera-action-buttons">
              <button
                className="btn-capture"
                onClick={captureImage}
                disabled={loading}
              >
                {loading ? "Uploading..." : "Capture"}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setIsCameraOpen(false)}
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
        <div className="uploaded-photos">
          <div className="uploaded-header">
            <p className="uploaded-heading">Uploaded photos</p>
            {filesArray.length > 0 && !localIsDeleting ? (
              <p
                onClick={() => setDeleteModal(true)}
                className="btn-delete-all"
              >
                Delete all
              </p>
            ) : (
              localIsDeleting && (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )
            )}
            {}
          </div>
          <div className="uploaded-main">
            {filesArray?.length > 0 ? filesArray?.map((file, i) => {
              return (
                <ImageListItem
                  key={i}
                  file={file}
                  index={i}
                  handleArchiveImages={handleArchiveImages}
                />
              );
            }) : <span className="mb-4">No Images Found</span>}
          </div>
        </div>
        <a
          href="https://youtu.be/COzILLQiiUw"
          target="_blank"
          className="quality-photos-link"
        >
          <div className="content-main">
            <p className="quality-label">How to take quality photos</p>
            <p className="quality-description">
              Watch our simple and quick explanation <br />
              on how to take quality photos
            </p>
          </div>
          <img src={qualitySvg} className="quality-img" alt="quality icon" />
        </a>
      </div>
      {deleteModal && (
        <Modal
          className={"widthResponsive"}
          isOpen={true}
          style={modalStyles as Styles}
        >
          <div className="delete-modal">
            <div className="body-main">
              <img src={dustbinSvg} className="dustbin-icon" />
              <p className="modal-text">Your are about to delete all photos</p>
            </div>
            <div className="delete-btn-main">
              <button
                onClick={() => setDeleteModal(false)}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button onClick={deleteAllImages} className="btn-delete">
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UploadPhotos;
